import { IPlacement, PlacementId } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { DuplicateRecordBanner } from "components/shared/duplicate-record-banner";
import { InputClientContact } from "components/shared/input-client-contact";
import { ContactRenderInfo } from "components/shared/input-contact-person";
import { ClientContactRelationship } from "generated/graphql";
import React, { useCallback } from "react";
import { useFeature } from "store/selectors/hooks";
import { ClientContacts } from "./client-contacts";

const mapBestContactPropsToClientProps: Record<string, string> = {
    name: "name",
    preferredName: "preferredName",
    cellPhone: "cellPhone",
    phone1: "phone",
    phoneNumbers: "phoneNumbers",
    email1: "email",
    email1OptOut: "emailOptOut",
    address1: "address",
    city: "city",
    state: "state",
    zip: "zip",
};

const mapBestContactPropsToIdealLocationProps: Record<string, string> = {
    address1: "bestLocationAddress",
    city: "bestLocationCity",
    state: "bestLocationState",
    zip: "bestLocationZip",
};

interface ClientBestContactProps {
    placement: IPlacement;
    onFieldChangeMulti: (fields: Record<string, unknown>) => void;
}

export const ClientAdditionalContacts: React.FC<{ clientId: PlacementId }> = props => (
    <InputFormBlock>
        <ClientContacts
            clientId={props.clientId}
            relationship={ClientContactRelationship.MoreContact}
        />
    </InputFormBlock>
);

export const ClientBestContactFields: React.FC<ClientBestContactProps> = props => {
    const { placement, onFieldChangeMulti } = props;
    const detailsPageVNext = useFeature("detailsPageVNext");
    const contactRenderInfoBestContactPerson: ContactRenderInfo = {
        contactGroupIcon: "46_MoreContacts",
        contactGroupHeading: "",
        showAddressFields: true,
        showRelationshipField: true,
        showRoleField: false,
        hideContactNotes: true,
        allowReferenceLink: false,
        specializedLayout: "Standard",
        allowPreferredName: true,
    };

    const handleFieldChange = useCallback(
        (fieldName: string, newValue: string) => {
            const newValues: Record<string, unknown> = {};

            // Need to actually update the field on the Best Contact object no matter what
            const bestContactKey = "bestContact";
            newValues[`${bestContactKey}.${fieldName}`] = newValue;

            // Check if we are also cloning the client. If so, we'll need to copy to
            // that corresponding prop too.
            if (
                placement.clientSameAsBestContact &&
                mapBestContactPropsToClientProps[fieldName]
            ) {
                newValues[`${mapBestContactPropsToClientProps[fieldName]}`] = newValue;
            }

            // Check if we're cloning the Best Contact address onto the Ideal Location Field.
            // If so, we'll need to update the corresponding ideal location field now too.
            if (
                placement.useBestContactAddressForBestLocation &&
                mapBestContactPropsToIdealLocationProps[fieldName]
            ) {
                newValues[`${mapBestContactPropsToIdealLocationProps[fieldName]}`] =
                    newValue;
            }

            onFieldChangeMulti(newValues);
        },
        [placement, onFieldChangeMulti],
    );

    return (
        <InputClientContact
            contact={placement.bestContactEntity}
            onFieldChange={handleFieldChange}
            headerLabel="Best Contact Person"
            contactRenderInfo={contactRenderInfoBestContactPerson}
            banner={
                detailsPageVNext ? undefined : (
                    <DuplicateRecordBanner
                        recordType="placement"
                        recordId={placement.id}
                        nameKey={placement.bestContactEntity?.name ?? ""}
                    />
                )
            }
            entityId={placement.id}
        />
    );
};

export const ClientBestContact: React.FC<ClientBestContactProps> = props => (
    <>
        <ClientBestContactFields {...props} />
        <ClientAdditionalContacts clientId={props.placement.id} />
    </>
);
